const PRICE_PER_SQM_DECIMAL_PLACES = 2;

const TOTAL_PRICE_SNAP_STEPS = [
  1_000_000_000_000,
  100_000_000_000,
  10_000_000_000,
  1_000_000_000,
  100_000_000,
  10_000_000,
  1_000_000,
  100_000,
  10_000,
  1_000,
  100,
  10,
  1,
] as const;

type ListingTotalPriceInput = {
  area: unknown;
  pricePerSqm: unknown;
  explicitTotalPrice?: unknown;
};

const toPositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Resolves the sale total returned by the listings API.
 *
 * The current API stores only a price per square metre rounded to two decimal
 * places. When the frontend reconstructs a total, that rounding can turn an
 * entered 550 000 ₽ into 549 997.50 ₽. We only snap a fractional reconstructed
 * value to a round total when that value is inside the maximum error introduced
 * by the API. An explicit total from a newer API contract always wins.
 */
export const resolveListingTotalPrice = ({
  area,
  pricePerSqm,
  explicitTotalPrice,
}: ListingTotalPriceInput): number | null => {
  const directTotal = toPositiveNumber(explicitTotalPrice);
  if (directTotal !== null) return directTotal;

  const parsedArea = toPositiveNumber(area);
  const parsedPricePerSqm = toPositiveNumber(pricePerSqm);
  if (parsedArea === null || parsedPricePerSqm === null) return null;

  const reconstructedTotal = parsedArea * parsedPricePerSqm;
  if (!Number.isFinite(reconstructedTotal) || reconstructedTotal <= 0) return null;
  if (Number.isInteger(reconstructedTotal)) return reconstructedTotal;

  const maximumRoundingError =
    (parsedArea * 10 ** -PRICE_PER_SQM_DECIMAL_PLACES) / 2;
  const comparisonEpsilon = Math.max(1, reconstructedTotal) * Number.EPSILON * 4;

  for (const step of TOTAL_PRICE_SNAP_STEPS) {
    const candidate = Math.round(reconstructedTotal / step) * step;
    if (
      candidate > 0 &&
      Math.abs(candidate - reconstructedTotal) <=
        maximumRoundingError + comparisonEpsilon
    ) {
      return candidate;
    }
  }

  return Math.round(reconstructedTotal);
};
